import { createChartOption } from 'components/BaseChart/utils/chartUtils';
import useImageObjectUrlCache from 'features/Integrations/hooks/useImageObjectUrlCache';
import {
  requestMapRecenter,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  buildStageDeviceChartModel,
  buildStageTooltipFormatter,
  getStageDevices,
  getSeriesType,
  getYAxisLabel,
} from '../utils/deviceGraph.utils';
import { bindTooltipCanvasClick } from '../utils/graphInteraction.utils';
import * as usgsEventUtils from '../utils/usgsEvent.utils';

export const useFloodGraphBuilder = ({
  title = 'Devices',
  devices = [],
  selectedDeviceMap = {},
  selectedDataType,
  unitType,
  setUnitType,
  startTime = undefined,
  endTime = undefined,
  historyDataLoading = false,
  dispatch = undefined,
  navigate = undefined,
  location = undefined,
  onSelectedDeviceIndex = undefined,
  onGraphDatasheetSelect = undefined,
  setShowCurrentDevice = undefined,
}) => {
  const tooltipParamsRef = useRef(null);
  const zrenderCleanupRef = useRef(null);
  const selectGraphDataPointRef = useRef(null);

  useEffect(
    () => () => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = null;
    },
    [],
  );

  const firstSelectedDevice = useMemo(() => {
    const stageDevices = getStageDevices(devices);
    const firstSelectedId = Object.keys(selectedDeviceMap || {}).find(
      (id) => selectedDeviceMap[id] && stageDevices.some((device) => String(device.id) === id),
    );
    return firstSelectedId
      ? (devices.find((device) => String(device.id) === firstSelectedId) ?? null)
      : null;
  }, [devices, selectedDeviceMap]);

  const { labels, datasets, markLineData } = useMemo(
    () =>
      buildStageDeviceChartModel({
        devices,
        selectedDeviceMap,
        selectedDataType,
        unitType,
        startTime,
        endTime,
      }),
    [devices, selectedDeviceMap, selectedDataType, unitType, startTime, endTime],
  );

  const hasData =
    Array.isArray(labels) && labels.length > 0 && Array.isArray(datasets) && datasets.length > 0;

  const alignedDatasets = useMemo(() => {
    if (!hasData) return [];

    return datasets.map((dataset) => {
      const rawPoints = dataset?._meta?.data || [];
      const byTimestampMap = new Map(rawPoints.map((point) => [point.ts, point]));

      return {
        ...dataset,
        _meta: { ...(dataset._meta || {}), byTimestampMap },
      };
    });
  }, [hasData, datasets]);

  useEffect(() => {
    tooltipParamsRef.current = null;
  }, [alignedDatasets]);

  const imageUrls = useMemo(() => {
    if (!hasData) return [];

    const urls = new Set();
    datasets.forEach((dataset) => {
      dataset?._meta?.data?.forEach((point) => {
        if (point?.img_link_url) urls.add(point.img_link_url);
      });
    });
    return [...urls];
  }, [hasData, datasets]);

  const imageMap = useImageObjectUrlCache(imageUrls);

  const seriesType = getSeriesType(selectedDataType);
  const yAxisLabel = getYAxisLabel(selectedDataType, unitType);

  const series = useMemo(() => {
    if (!hasData) return [];

    return alignedDatasets.map((dataset) => ({
      name: dataset.label,
      type: seriesType,
      data: dataset.data,
      showSymbol: false,
      symbol: 'circle',
      symbolSize: (value) => {
        const timestamp = Array.isArray(value) ? value[0] : null;
        const raw = dataset?._meta?.byTimestampMap?.get(timestamp);
        return raw?.did != null || raw?.ts != null ? 6 : 0;
      },
      connectNulls: true,
      smooth: false,
      itemStyle: {
        color: dataset.color,
      },
      lineStyle: {
        width: 2,
      },
    }));
  }, [hasData, alignedDatasets, seriesType]);

  const tooltipFormatter = useMemo(() => {
    if (!hasData) return null;
    const formatTooltip = buildStageTooltipFormatter(alignedDatasets, imageMap, unitType);
    return (params) => {
      tooltipParamsRef.current = params;
      return formatTooltip(params);
    };
  }, [hasData, alignedDatasets, imageMap, unitType]);

  const xAxisLabelFormatter = useMemo(() => {
    const formatter = new Intl.DateTimeFormat([], {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return (value) => formatter.format(new Date(value)).replace(', ', ' ');
  }, []);

  const option = useMemo(() => {
    if (!hasData) return null;
    return createChartOption({
      title,
      titleTop: 8,
      xAxisLabels: labels,
      minValue: startTime,
      maxValue: endTime,
      series,
      gridPadding: {
        left: '10%',
        right: '2%',
        top: '26%',
        bottom: '18%',
      },
      tooltipFormatter: tooltipFormatter || undefined,
      xAxisRotate: 0,
      xAxisLabelFontSize: 10,
      xAxisLabelHideOverlap: true,
      xAxisLabelInterval: 'auto',
      xAxisLabelType: 'time',
      xAxisLabelFormatter,
      dataZoomEnabled: true,
      markLineData,
      yAxisLabel,
    });
  }, [
    title,
    hasData,
    labels,
    series,
    tooltipFormatter,
    markLineData,
    yAxisLabel,
    startTime,
    endTime,
    xAxisLabelFormatter,
  ]);

  const selectGraphDataPoint = useCallback(
    (params) => {
      if (!params) return;

      const seriesIndex = params.seriesIndex ?? 0;
      const dataset = alignedDatasets[seriesIndex] || datasets[seriesIndex] || {};
      const targetId = dataset?._meta?.id;

      if (!targetId) return;

      const dataRowId = usgsEventUtils.resolveDataRowIdFromParams(params, dataset?._meta);
      if (dataRowId == null) return;

      try {
        usgsEventUtils.setUidDidInUrl(targetId, dataRowId, navigate, location);
      } catch (e) {
        // URL updates are non-critical for graph selection.
      }

      onGraphDatasheetSelect?.(targetId);
      dispatch?.(setSelectedDeviceId(targetId));
      dispatch?.(requestMapRecenter());
      dispatch?.(setSelectedDevice({ id: targetId }));

      const globalIndex = (devices || []).findIndex((device) => device.id === targetId);
      if (globalIndex >= 0) {
        onSelectedDeviceIndex?.(globalIndex);
        setShowCurrentDevice?.(true);
      }
    },
    [
      alignedDatasets,
      datasets,
      devices,
      onSelectedDeviceIndex,
      onGraphDatasheetSelect,
      setShowCurrentDevice,
      navigate,
      location,
      dispatch,
    ],
  );

  useEffect(() => {
    selectGraphDataPointRef.current = selectGraphDataPoint;
  }, [selectGraphDataPoint]);

  const onChartClick = useMemo(
    () => (params) => {
      if (!params || params.componentType !== 'series') return;
      selectGraphDataPoint(params);
    },
    [selectGraphDataPoint],
  );

  const onChartReady = useMemo(
    () => (chartInstance) => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = bindTooltipCanvasClick(
        chartInstance,
        () => tooltipParamsRef.current,
        (tooltipParam) => selectGraphDataPointRef.current?.(tooltipParam),
      );
    },
    [],
  );

  if (!option) {
    if (historyDataLoading) {
      return { option: null, loading: true, loadingMessage: 'Loading device data...' };
    }

    if (firstSelectedDevice) {
      return { option: null, fallbackDatasheetDevice: firstSelectedDevice };
    }

    return { option: null, emptyMessage: 'No device selected' };
  }

  return {
    option,
    datasets,
    onChartClick,
    onChartReady,
    controlsLeft: [
      {
        key: 'units',
        label: `Units: ${unitType ? 'Metric (m)' : 'Imperial (ft)'}`,
        className: 'm-2 px-2 py-1',
        onClick: () => setUnitType?.((prev) => !prev),
      },
    ],
  };
};
